// POS-report import for the End of Period wizard.
//
// Pure helpers shared by the Import POS Report step so the mapping and
// fill-in behavior is testable in isolation. The flow:
//
//   1. The user uploads an end-of-period report exported from their POS
//      (Toast, Lightspeed, Aloha, Micros…). The eop-pos-import edge function
//      extracts totals + sales-by-category and proposes a P&L category match
//      for each report category.
//   2. normalizePosExtraction() cleans that response into a stable shape.
//   3. The user reviews/edits the proposed category mapping in the UI.
//   4. buildPosApplyPlan() turns the extraction + final mapping into the
//      concrete values to write into the wizard: per-P&L-category gross
//      sales and deductions, plus report-level deduction totals that can't
//      be assigned to a category (those land in Unallocated Adjustments).
//
// Amounts the report doesn't state stay null — they are never invented or
// defaulted to zero, matching the wizard's data-status philosophy.

const round2 = (n) => Math.round(n * 100) / 100;

// Monetary values from the extraction: accept numbers or numeric strings,
// treat negatives as their magnitude (POS reports often print deductions as
// negatives), anything else is "not stated".
export const posAmount = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? round2(Math.abs(n)) : null;
};

const cleanISODate = (v) => {
  if (typeof v !== 'string') return null;
  const m = v.match(/^\d{4}-\d{2}-\d{2}/);
  return m ? m[0] : null;
};

/**
 * Normalize the eop-pos-import edge-function response. `pnlCategories` is the
 * store's category list ([{ id, name }]) — matched ids not in it are dropped.
 */
export function normalizePosExtraction(raw, pnlCategories = []) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const validIds = new Set((pnlCategories || []).map((c) => String(c.id)));

  const categories = (Array.isArray(src.categories) ? src.categories : [])
    .filter((c) => c && typeof c.name === 'string' && c.name.trim())
    .map((c) => {
      const matchedRaw = c.matched_pnl_category_id != null ? String(c.matched_pnl_category_id) : null;
      const matchedCatId = matchedRaw && validIds.has(matchedRaw) ? matchedRaw : null;
      return {
        name: c.name.trim(),
        grossSales: posAmount(c.gross_sales),
        discounts: posAmount(c.discounts),
        comps: posAmount(c.comps),
        refunds: posAmount(c.refunds),
        matchedCatId,
        confidence: matchedCatId ? (c.match_confidence || 'low') : 'none',
      };
    })
    // A category line with no stated amount at all has nothing to import.
    .filter((c) => c.grossSales != null || c.discounts != null || c.comps != null || c.refunds != null);

  return {
    posSystem: typeof src.pos_system === 'string' && src.pos_system.trim() ? src.pos_system.trim() : null,
    periodStart: cleanISODate(src.report_period_start),
    periodEnd: cleanISODate(src.report_period_end),
    categorySalesBasis: ['gross', 'net'].includes(src.category_sales_basis) ? src.category_sales_basis : 'unknown',
    categories,
    totals: {
      grossSales: posAmount(src.gross_sales_total),
      netSales: posAmount(src.net_sales_total),
      discounts: posAmount(src.discounts_total),
      comps: posAmount(src.comps_total),
      refunds: posAmount(src.refunds_total),
      taxes: posAmount(src.taxes_total),
      serviceCharges: posAmount(src.service_charges_total),
    },
    warnings: (Array.isArray(src.warnings) ? src.warnings : [])
      .filter((w) => typeof w === 'string' && w.trim())
      .map((w) => w.trim()),
  };
}

/**
 * Map a clover-sales-pull response into the SAME JSON shape the eop-pos-import
 * edge function returns from the AI, so Clover sales flow through the exact
 * same normalize → review-mapping → apply pipeline as an uploaded POS report.
 *
 * One row per *Clover* sales category (from `clover_categories`, the raw
 * gross-by-category breakdown), named by its Clover category name. A category
 * whose name lines up with one of the store's P&L categories comes back
 * pre-matched at high confidence (mirroring the server's name match); a Clover
 * category with no P&L counterpart comes back UNMATCHED, so it shows up in the
 * row-based assignment tool for the user to map instead of being silently
 * dropped — and an all-unmatched pull no longer dead-ends. The shared mapping
 * table and the store's saved report-category mappings (keyed by the Clover
 * category name) resolve the rest, exactly like an uploaded report.
 *
 * If an older clover-sales-pull response omits `clover_categories`, fall back to
 * the pre-resolved `category_sales` (keyed by P&L category id).
 *
 * Deductions (discounts/comps/refunds), tax, and service charges aren't part
 * of the Clover sales pull, so those totals stay null — never invented — and
 * are entered on the later wizard steps, exactly like a POS report that only
 * prints sales by category. Pass the result to normalizePosExtraction() before
 * use, same as the edge-function response.
 *
 * `cloverResult` is the clover-sales-pull body:
 *   { net_sales_total, clover_categories: { [cloverCatName]: usd },
 *     category_sales: { [pnlCatId]: usd }, period_start, period_end, … }
 * `pnlCategories` is the store's [{ id, name }] list — it names/pre-matches each
 * line and drops fallback sales mapped to a P&L category that no longer exists.
 */
export function cloverSalesToPosExtract(cloverResult, pnlCategories = []) {
  const src = cloverResult && typeof cloverResult === 'object' ? cloverResult : {};
  const cloverCats = src.clover_categories && typeof src.clover_categories === 'object'
    ? src.clover_categories
    : null;

  let categories;
  if (cloverCats && Object.keys(cloverCats).length > 0) {
    // Case-insensitive P&L-id lookup by name, mirroring the server's name match
    // so a Clover category that lines up by name comes back pre-selected.
    const idByName = new Map(
      (pnlCategories || []).map((c) => [String(c.name).trim().toLowerCase(), String(c.id)]),
    );
    categories = Object.entries(cloverCats).map(([cloverName, amount]) => {
      const matched = idByName.get(String(cloverName).trim().toLowerCase()) || null;
      return {
        name: cloverName,
        gross_sales: amount,
        discounts: null,
        comps: null,
        refunds: null,
        matched_pnl_category_id: matched,
        match_confidence: matched ? 'high' : 'none',
      };
    });
  } else {
    // Fallback for responses without a raw category breakdown: use the
    // pre-resolved P&L totals, named by the store's P&L list.
    const nameById = new Map((pnlCategories || []).map((c) => [String(c.id), c.name]));
    const categorySales = src.category_sales && typeof src.category_sales === 'object' ? src.category_sales : {};
    categories = Object.entries(categorySales)
      .map(([pnlId, amount]) => {
        const id = String(pnlId);
        const name = nameById.get(id);
        if (!name) return null; // mapped to a P&L category that's since been deleted
        return {
          name,
          gross_sales: amount,
          discounts: null,
          comps: null,
          refunds: null,
          matched_pnl_category_id: id,
          match_confidence: 'high',
        };
      })
      .filter(Boolean);
  }

  return {
    pos_system: 'Clover',
    report_period_start: src.period_start ?? null,
    report_period_end: src.period_end ?? null,
    gross_sales_total: null,
    net_sales_total: src.net_sales_total ?? null,
    discounts_total: null,
    comps_total: null,
    refunds_total: null,
    taxes_total: null,
    service_charges_total: null,
    category_sales_basis: 'gross',
    categories,
    warnings: [
      'Clover provides sales by category only — enter discounts, comps, and refunds on the later steps.',
    ],
  };
}

/**
 * Map a square-sales-pull response into the SAME JSON shape the eop-pos-import
 * edge function returns, so Square sales flow through the identical normalize →
 * review-mapping → apply pipeline as an uploaded report or a Clover pull.
 *
 * One row per *Square* sales category (from `square_categories`, the raw
 * gross-by-category breakdown), named by its Square category name. A category
 * whose name lines up with one of the store's P&L categories comes back
 * pre-matched at high confidence; one with no P&L counterpart comes back
 * UNMATCHED so it surfaces in the row-based assignment tool instead of being
 * dropped. Falls back to the pre-resolved `category_sales` (keyed by P&L id)
 * for responses without a raw breakdown. Mirrors cloverSalesToPosExtract.
 *
 * Deductions (discounts/comps/refunds), tax, and service charges aren't part of
 * the Square sales pull, so those totals stay null and are entered on the later
 * wizard steps. Pass the result to normalizePosExtraction() before use.
 */
export function squareSalesToPosExtract(squareResult, pnlCategories = []) {
  const src = squareResult && typeof squareResult === 'object' ? squareResult : {};
  const squareCats = src.square_categories && typeof src.square_categories === 'object'
    ? src.square_categories
    : null;

  let categories;
  if (squareCats && Object.keys(squareCats).length > 0) {
    const idByName = new Map(
      (pnlCategories || []).map((c) => [String(c.name).trim().toLowerCase(), String(c.id)]),
    );
    categories = Object.entries(squareCats).map(([squareName, amount]) => {
      const matched = idByName.get(String(squareName).trim().toLowerCase()) || null;
      return {
        name: squareName,
        gross_sales: amount,
        discounts: null,
        comps: null,
        refunds: null,
        matched_pnl_category_id: matched,
        match_confidence: matched ? 'high' : 'none',
      };
    });
  } else {
    const nameById = new Map((pnlCategories || []).map((c) => [String(c.id), c.name]));
    const categorySales = src.category_sales && typeof src.category_sales === 'object' ? src.category_sales : {};
    categories = Object.entries(categorySales)
      .map(([pnlId, amount]) => {
        const id = String(pnlId);
        const name = nameById.get(id);
        if (!name) return null; // mapped to a P&L category that's since been deleted
        return {
          name,
          gross_sales: amount,
          discounts: null,
          comps: null,
          refunds: null,
          matched_pnl_category_id: id,
          match_confidence: 'high',
        };
      })
      .filter(Boolean);
  }

  return {
    pos_system: 'Square',
    report_period_start: src.period_start ?? null,
    report_period_end: src.period_end ?? null,
    gross_sales_total: null,
    net_sales_total: src.net_sales_total ?? null,
    discounts_total: null,
    comps_total: null,
    refunds_total: null,
    taxes_total: null,
    service_charges_total: null,
    category_sales_basis: 'gross',
    categories,
    warnings: [
      'Square provides sales by category only — enter discounts, comps, and refunds on the later steps.',
    ],
  };
}

/**
 * Lookup key for a report category name in pos_report_category_mappings —
 * must mirror the normalized_name column: lowercased, trimmed, internal
 * whitespace collapsed.
 */
export const posCategoryKey = (name) => (
  typeof name === 'string' ? name.trim().replace(/\s+/g, ' ').toLowerCase() : ''
);

/**
 * Seed the wizard's mappingByIndex from the store's saved report-category
 * mappings, so a re-uploaded report remembers the user's past corrections.
 *
 * `savedMappings` is { normalizedName: pnlCatId (string) | null } — null
 * meaning the user chose not to import that report category. A saved id
 * that no longer exists in `pnlCategories` is ignored (the P&L category was
 * deleted), leaving the line to the AI match. Names with no saved row also
 * fall through to the AI match — only user-confirmed choices are seeded.
 */
export function buildInitialPosMapping(extraction, savedMappings = {}, pnlCategories = []) {
  const validIds = new Set((pnlCategories || []).map((c) => String(c.id)));
  const mapping = {};
  (extraction?.categories || []).forEach((cat, idx) => {
    const key = posCategoryKey(cat.name);
    if (!Object.prototype.hasOwnProperty.call(savedMappings, key)) return;
    const saved = savedMappings[key];
    if (saved == null) mapping[idx] = '';
    else if (validIds.has(String(saved))) mapping[idx] = String(saved);
  });
  return mapping;
}

const DEDUCTION_FIELDS = ['discounts', 'comps', 'refunds'];

/**
 * Turn a normalized extraction plus the user's final category mapping into
 * the values the wizard applies.
 *
 * `mappingByIndex` maps a category's index in `extraction.categories` to the
 * chosen P&L category id (string), or '' / null for "don't import this line".
 * Defaults to each category's AI-proposed match when an index is absent.
 *
 * Returns:
 *   {
 *     byCat:       { [pnlCatId]: { gross, discounts, comps, refunds } },  // null = not in report
 *     unallocated: { discounts, comps, refunds },  // report totals not assigned to a category (null = not stated)
 *     skipped:     [{ name, amount }],             // extracted sales lines left unmapped
 *     appliedCategoryCount,
 *   }
 */
export function buildPosApplyPlan(extraction, mappingByIndex = {}) {
  const byCat = {};
  const skipped = [];

  (extraction?.categories || []).forEach((cat, idx) => {
    const choice = Object.prototype.hasOwnProperty.call(mappingByIndex, idx)
      ? mappingByIndex[idx]
      : cat.matchedCatId;
    const catId = choice ? String(choice) : null;
    if (!catId) {
      if (cat.grossSales != null) skipped.push({ name: cat.name, amount: cat.grossSales });
      return;
    }
    const target = byCat[catId] || (byCat[catId] = { gross: null, discounts: null, comps: null, refunds: null });
    if (cat.grossSales != null) target.gross = round2((target.gross ?? 0) + cat.grossSales);
    DEDUCTION_FIELDS.forEach((f) => {
      if (cat[f] != null) target[f] = round2((target[f] ?? 0) + cat[f]);
    });
  });

  // Whatever part of the report-level deduction totals was NOT assigned to a
  // category goes to Unallocated Adjustments — the report told us the money
  // moved, just not which category it belongs to.
  const unallocated = { discounts: null, comps: null, refunds: null };
  DEDUCTION_FIELDS.forEach((f) => {
    const total = extraction?.totals?.[f];
    if (total == null) return;
    const allocated = Object.values(byCat).reduce((s, t) => s + (t[f] ?? 0), 0);
    unallocated[f] = Math.max(0, round2(total - allocated));
  });

  return {
    byCat,
    unallocated,
    skipped,
    appliedCategoryCount: Object.keys(byCat).length,
  };
}

/**
 * Split an unallocated deduction total across P&L categories in proportion
 * to their gross sales — the "divide it up for me" option when a report only
 * gives a period total. A category with 40% of sales takes 40% of the amount.
 *
 * `salesByCat` is { [pnlCatId]: grossSales }; entries without positive sales
 * are ignored. Returns { [pnlCatId]: amount } whose values sum to exactly
 * `total` (cent-rounding drift lands on the largest-sales category), or null
 * when the total or the sales to base shares on are missing.
 */
export function allocateProportionally(total, salesByCat = {}) {
  const amount = typeof total === 'number' ? total : parseFloat(total);
  if (!Number.isFinite(amount) || amount <= 0) return null;

  const entries = Object.entries(salesByCat)
    .map(([k, v]) => [k, typeof v === 'number' ? v : parseFloat(v)])
    .filter(([, v]) => Number.isFinite(v) && v > 0);
  if (entries.length === 0) return null;

  const salesSum = entries.reduce((s, [, v]) => s + v, 0);
  const shares = {};
  entries.forEach(([k, v]) => { shares[k] = round2((amount * v) / salesSum); });

  const drift = round2(amount - Object.values(shares).reduce((s, v) => s + v, 0));
  if (drift !== 0) {
    const [largest] = entries.reduce((a, b) => (b[1] > a[1] ? b : a));
    shares[largest] = round2(shares[largest] + drift);
  }
  return shares;
}
